/**
 * usePaymentInfo フック
 *
 * 問い合わせに紐づく支払い情報を取得するカスタムフック
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  paymentRepository,
  type PaymentInfo,
  type EstimateInfo,
  type PaymentOrEstimateInfo,
  isPaymentInfo,
  isEstimateInfo,
} from '../data/paymentRepository';

// デバッグログをFlutter WebViewに送信
const sendDebugLog = (message: string): void => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).DebugChannel;
    if (webview?.postMessage) {
      webview.postMessage(message);
    }
    console.log('[usePaymentInfo]', message);
  } catch {
    // WebView外での実行時は無視
  }
};

interface UsePaymentInfoResult {
  /** 支払い情報（登録済みの場合のみ） */
  paymentInfo: PaymentInfo | null;
  /** 見積もり情報（支払い未登録の場合のみ） */
  estimateInfo: EstimateInfo | null;
  /** 生データ */
  data: PaymentOrEstimateInfo;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * 支払い情報取得フック
 * @param inquiryId 問い合わせID
 * @param token 認証トークン
 */
export const usePaymentInfo = (
  inquiryId: string,
  token?: string
): UsePaymentInfoResult => {
  const [data, setData] = useState<PaymentOrEstimateInfo>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 前回のinquiryIdを保持（同期的な変化検知用）
  const prevInquiryIdRef = useRef<string>(inquiryId);

  // inquiryIdが変わったかを同期的にチェック
  const inquiryIdChanged = prevInquiryIdRef.current !== inquiryId;
  if (inquiryIdChanged) {
    prevInquiryIdRef.current = inquiryId;
  }

  // inquiryIdが空から有効値に変わった場合、このレンダリングではローディング中とみなす
  const isPendingFetch = inquiryIdChanged && inquiryId !== '';

  const fetchPaymentInfo = useCallback(async () => {
    if (!inquiryId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await paymentRepository.getPayment(inquiryId, token);

    // デバッグログ（Flutter WebViewに送信）
    sendDebugLog(`API result: ${JSON.stringify(result)}`);
    sendDebugLog(`isPaymentInfo: ${result.success ? isPaymentInfo(result.data) : 'N/A'}`);
    sendDebugLog(`isEstimateInfo: ${result.success ? isEstimateInfo(result.data) : 'N/A'}`);

    if (result.success) {
      setData(result.data);
    } else {
      setError(result.error.message);
    }

    setIsLoading(false);
  }, [inquiryId, token]);

  useEffect(() => {
    fetchPaymentInfo();
  }, [fetchPaymentInfo]);

  return {
    paymentInfo: isPaymentInfo(data) ? data : null,
    estimateInfo: isEstimateInfo(data) ? data : null,
    data,
    // isPendingFetchの場合は、useEffectでfetchが始まる前だがローディング中とみなす
    isLoading: isLoading || isPendingFetch,
    error,
    refetch: fetchPaymentInfo,
  };
};
